import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import grapesjs from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";
import { GetData, PutData, PostData } from "../../services/api";
import Swal from "sweetalert2";
import FormConfigModal from "./FormConfigModal";
import { LP_FORM_HANDLER_SCRIPT } from "./lpFormHandler";

// ─── CSS SANITIZERS ────────────────────────────────────────────────────────────

function sanitizeCssForEditor(css) {
  // 1. Neutralizar reglas con opacity:0 o visibility:hidden (pre-reveal)
  css = css.replace(/(\{[^}]*)\bopacity\s*:\s*0\b(?!\.\d)/g, "$1opacity:1");
  css = css.replace(/(\{[^}]*)\bvisibility\s*:\s*hidden/g, "$1visibility:visible");

  // 2. Neutralizar transform:translateY(100%) (sticky bars ocultas)
  css = css.replace(
    /(\{[^}]*)\btransform\s*:\s*translateY\(\s*100%\s*\)/g,
    "$1transform:translateY(0)"
  );

  // 3. Neutralizar display:none SOLO en reglas que también tengan position:fixed
  css = css.replace(
    /([^{}]*position\s*:\s*fixed[^}]*)\bdisplay\s*:\s*none/g,
    "$1display:block"
  );
  css = css.replace(
    /([^{}]*display\s*:\s*none[^}]*)\bposition\s*:\s*fixed/g,
    "$1position:relative"
  );

  // 4. Neutralizar @keyframes que animan desde opacity:0
  css = css.replace(
    /(@keyframes\s+\w+\s*\{[^}]*from\s*\{[^}]*)opacity\s*:\s*0/gi,
    "$1opacity:1"
  );

  // 5. Neutralizar overflow:hidden en selectores html/body (bloquea scroll del editor)
  css = css.replace(
    /((?:html|body)\b[^{]*\{[^}]*)\boverflow\s*:\s*hidden/gi,
    "$1overflow:auto"
  );
  css = css.replace(
    /((?:html|body)\b[^{]*\{[^}]*)\boverflow-x\s*:\s*hidden/gi,
    "$1overflow-x:auto"
  );
  // 6. Neutralizar height:100vh/100% en html/body (fija el viewport y bloquea scroll)
  css = css.replace(
    /((?:html|body)\b[^{]*\{[^}]*)\bheight\s*:\s*100(?:vh|%)/gi,
    "$1height:auto"
  );

  return css;
}

function sanitizeDomForEditor(doc) {
  doc.querySelectorAll("*").forEach((el) => {
    // 1. Eliminar event handlers inline
    Array.from(el.attributes).forEach((attr) => {
      if (/^on\w+/i.test(attr.name)) el.removeAttribute(attr.name);
    });

    // 2. Forzar visible si tiene opacity:0 o visibility:hidden inline
    if (el.style?.opacity === "0") el.style.opacity = "1";
    if (el.style?.visibility === "hidden") el.style.visibility = "visible";

    // 3. Clases "oculto hasta JS"
    const cls = el.className;
    if (
      typeof cls === "string" &&
      /\b(anim-ready|anim-hidden|scroll-reveal|fade-out|is-hidden|not-visible|pre-anim|invisible)\b/i.test(
        cls
      )
    ) {
      el.style.opacity = "1";
      el.style.visibility = "visible";
      el.style.transform = "none";
    }

    // 4. Sticky bars ocultas (position:fixed + translateY(100%) o display:none)
    if (el.style?.position === "fixed" || /\bsticky\b/i.test(cls || "")) {
      if (el.style.transform?.includes("100%")) {
        el.style.transform = "translateY(0)";
      }
      if (el.style.display === "none") el.style.display = "block";
      // Convertir fixed a relative para que no flote sobre el canvas
      el.style.position = "relative";
    }
  });

  // 5. Countdowns placeholder
  doc.querySelectorAll("[data-target]").forEach((el) => {
    el.querySelectorAll('[id*="day"], [id*="hour"], [id*="min"], [id*="sec"]').forEach(
      (span) => {
        if (span.textContent === "--" || span.textContent?.trim() === "") {
          const id = span.id.toLowerCase();
          if (id.includes("day")) span.textContent = "03";
          else if (id.includes("hour")) span.textContent = "12";
          else if (id.includes("min")) span.textContent = "45";
          else if (id.includes("sec")) span.textContent = "30";
        }
      }
    );
  });

  // 6. Year spans
  doc.querySelectorAll("#year, [data-year], .current-year").forEach((el) => {
    if (!el.textContent?.trim()) el.textContent = String(new Date().getFullYear());
  });
}

// ─── LANDING BLOCKS ────────────────────────────────────────────────────────────

function registerLandingBlocks(editor) {
  const bm = editor.BlockManager;
  const blocks = [
    {
      id: "lp-hero",
      label: "🎯 Hero claro",
      content: `<section style="padding:80px 24px;text-align:center;background:#ffffff;"><h1 style="font-size:clamp(32px,5vw,58px);font-weight:900;line-height:1.08;margin:0 0 16px;color:#1a1a1a;">Tu titular principal aquí</h1><p style="font-size:18px;color:#555;max-width:540px;margin:0 auto 36px;line-height:1.65;">Describe el beneficio principal de tu oferta.</p><a href="#" style="display:inline-block;background:#6C63FF;color:#fff;padding:16px 40px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;">Quiero acceso ahora →</a></section>`,
    },
    {
      id: "lp-hero-dark",
      label: "🌑 Hero oscuro",
      content: `<section style="padding:80px 24px;text-align:center;background:#0f0f1a;"><p style="font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:#8888bb;margin:0 0 14px;font-weight:600;">✦ oferta limitada</p><h1 style="font-size:clamp(30px,5vw,56px);font-weight:900;line-height:1.08;margin:0 0 16px;color:#f0f0ff;">La solución que estabas <span style="color:#6C63FF;">buscando</span></h1><p style="font-size:17px;color:#9999bb;max-width:500px;margin:0 auto 36px;line-height:1.65;">Subtítulo con la promesa y el beneficio clave de tu oferta.</p><a href="#" style="display:inline-block;background:#6C63FF;color:#fff;padding:16px 40px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;">Comenzar ahora →</a></section>`,
    },
    {
      id: "lp-features-3",
      label: "⊞ Beneficios 3 col",
      content: `<section style="padding:64px 24px;background:#fafafa;"><h2 style="text-align:center;font-size:clamp(24px,3.5vw,38px);font-weight:800;margin:0 0 10px;color:#1a1a1a;">¿Por qué elegir esto?</h2><p style="text-align:center;color:#666;font-size:16px;margin:0 0 48px;">El subtítulo de beneficios.</p><div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:24px;max-width:960px;margin:0 auto;"><div style="background:#fff;border-radius:12px;padding:28px 24px;box-shadow:0 2px 16px rgba(0,0,0,0.06);"><div style="font-size:32px;margin-bottom:12px;">⚡</div><h3 style="font-size:18px;font-weight:700;margin:0 0 8px;color:#1a1a1a;">Resultado rápido</h3><p style="font-size:14px;color:#666;line-height:1.6;margin:0;">Descripción del primer beneficio clave.</p></div><div style="background:#fff;border-radius:12px;padding:28px 24px;box-shadow:0 2px 16px rgba(0,0,0,0.06);"><div style="font-size:32px;margin-bottom:12px;">🎯</div><h3 style="font-size:18px;font-weight:700;margin:0 0 8px;color:#1a1a1a;">Precisión probada</h3><p style="font-size:14px;color:#666;line-height:1.6;margin:0;">Descripción del segundo beneficio clave.</p></div><div style="background:#fff;border-radius:12px;padding:28px 24px;box-shadow:0 2px 16px rgba(0,0,0,0.06);"><div style="font-size:32px;margin-bottom:12px;">🔒</div><h3 style="font-size:18px;font-weight:700;margin:0 0 8px;color:#1a1a1a;">100% garantizado</h3><p style="font-size:14px;color:#666;line-height:1.6;margin:0;">Descripción del tercer beneficio clave.</p></div></div></section>`,
    },
    {
      id: "lp-testimonial",
      label: "💬 Testimonio",
      content: `<section style="padding:64px 24px;background:#fff;"><div style="max-width:680px;margin:0 auto;text-align:center;"><div style="font-size:28px;color:#6C63FF;margin-bottom:16px;">❝</div><p style="font-size:20px;font-style:italic;color:#2a2a2a;line-height:1.6;margin:0 0 24px;">"Escribe aquí el testimonio real de un cliente satisfecho."</p><div style="display:flex;align-items:center;justify-content:center;gap:14px;"><div style="width:48px;height:48px;border-radius:50%;background:#e0e0e0;overflow:hidden;flex-shrink:0;"></div><div style="text-align:left;"><p style="font-weight:700;color:#1a1a1a;margin:0;font-size:15px;">Nombre Apellido</p><p style="color:#888;font-size:13px;margin:0;">Cargo o descripción</p></div></div></div></section>`,
    },
    {
      id: "lp-testimonials-grid",
      label: "💬 Testimonios grid",
      content: `<section style="padding:64px 24px;background:#f8f8fb;"><h2 style="text-align:center;font-size:clamp(22px,3vw,34px);font-weight:800;margin:0 0 40px;color:#1a1a1a;">Lo que dicen nuestros clientes</h2><div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:20px;max-width:960px;margin:0 auto;"><div style="background:#fff;border-radius:12px;padding:24px;box-shadow:0 2px 12px rgba(0,0,0,0.07);"><p style="color:#ffb84d;font-size:13px;margin:0 0 12px;">★★★★★</p><p style="font-size:14px;color:#333;line-height:1.65;margin:0 0 16px;">"Testimonio uno. Muy específico y con resultado concreto."</p><p style="font-weight:700;font-size:13px;color:#1a1a1a;margin:0;">— Ana García</p></div><div style="background:#fff;border-radius:12px;padding:24px;box-shadow:0 2px 12px rgba(0,0,0,0.07);"><p style="color:#ffb84d;font-size:13px;margin:0 0 12px;">★★★★★</p><p style="font-size:14px;color:#333;line-height:1.65;margin:0 0 16px;">"Testimonio dos. El antes y el después de usar el producto."</p><p style="font-weight:700;font-size:13px;color:#1a1a1a;margin:0;">— Carlos Ruiz</p></div><div style="background:#fff;border-radius:12px;padding:24px;box-shadow:0 2px 12px rgba(0,0,0,0.07);"><p style="color:#ffb84d;font-size:13px;margin:0 0 12px;">★★★★★</p><p style="font-size:14px;color:#333;line-height:1.65;margin:0 0 16px;">"Testimonio tres. La transformación o resultado específico."</p><p style="font-weight:700;font-size:13px;color:#1a1a1a;margin:0;">— María López</p></div></div></section>`,
    },
    {
      id: "lp-cta-section",
      label: "🚀 CTA urgencia",
      content: `<section style="padding:72px 24px;background:#6C63FF;text-align:center;"><h2 style="font-size:clamp(26px,4vw,44px);font-weight:900;color:#fff;margin:0 0 14px;line-height:1.1;">Es el momento de actuar</h2><p style="font-size:17px;color:rgba(255,255,255,0.85);max-width:480px;margin:0 auto 36px;line-height:1.6;">No lo dejes para mañana. Esta oferta tiene cupos limitados.</p><a href="#" style="display:inline-block;background:#fff;color:#6C63FF;padding:18px 48px;border-radius:10px;font-size:17px;font-weight:800;text-decoration:none;">Quiero empezar hoy →</a><p style="margin:16px 0 0;font-size:12px;color:rgba(255,255,255,0.6);">Sin compromisos · Garantía incluida · Acceso inmediato</p></section>`,
    },
    {
      id: "lp-video-yt",
      label: "▶ Video YouTube",
      content: `<section style="padding:64px 24px;background:#fff;text-align:center;"><h2 style="font-size:clamp(22px,3vw,34px);font-weight:800;margin:0 0 8px;color:#1a1a1a;">Mira cómo funciona</h2><p style="color:#666;font-size:15px;margin:0 0 32px;">Descripción breve de lo que verás en el video.</p><div style="position:relative;width:100%;max-width:800px;margin:0 auto;aspect-ratio:16/9;"><iframe src="https://www.youtube.com/embed/dQw4w9WgXcQ" referrerpolicy="strict-origin-when-cross-origin" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen" title="Video" style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;border-radius:12px;"></iframe></div></section>`,
    },
    {
      id: "lp-img-hero",
      label: "🖼 Imagen hero",
      content: `<div style="width:100%;max-width:960px;margin:0 auto;padding:0 24px;"><img src="" alt="Imagen principal" style="width:100%;aspect-ratio:16/9;object-fit:cover;border-radius:12px;display:block;"/></div>`,
    },
    {
      id: "lp-img-slot",
      label: "🖼 Imagen slot",
      content: `<div style="width:100%;padding:0 24px;max-width:960px;margin:0 auto;"><img src="" alt="Imagen de apoyo" style="width:100%;max-height:360px;object-fit:cover;border-radius:10px;display:block;"/></div>`,
    },
    {
      id: "lp-2col",
      label: "⬛ 2 columnas",
      content: `<section style="padding:64px 24px;background:#fff;"><div style="max-width:960px;margin:0 auto;display:grid;grid-template-columns:1fr 1fr;gap:48px;align-items:center;"><div><h2 style="font-size:clamp(22px,3vw,34px);font-weight:800;margin:0 0 14px;color:#1a1a1a;">Titular de sección</h2><p style="font-size:15px;color:#555;line-height:1.7;margin:0 0 24px;">Describe aquí el valor, la historia o el mecanismo de tu producto.</p><a href="#" style="display:inline-block;background:#6C63FF;color:#fff;padding:13px 28px;border-radius:8px;font-size:14px;font-weight:700;text-decoration:none;">Ver más →</a></div><div><img src="" alt="Imagen" style="width:100%;border-radius:12px;display:block;"/></div></div></section>`,
    },
    {
      id: "lp-faq",
      label: "❓ FAQ acordeón",
      content: `<section style="padding:64px 24px;background:#fafafa;"><h2 style="text-align:center;font-size:clamp(22px,3vw,34px);font-weight:800;margin:0 0 40px;color:#1a1a1a;">Preguntas frecuentes</h2><div style="max-width:680px;margin:0 auto;display:flex;flex-direction:column;gap:12px;"><details style="background:#fff;border-radius:10px;padding:20px 24px;cursor:pointer;"><summary style="font-weight:700;font-size:15px;color:#1a1a1a;list-style:none;">¿Cuánto tiempo tarda en funcionar?</summary><p style="font-size:14px;color:#555;margin:12px 0 0;line-height:1.65;">Respuesta a la primera pregunta frecuente.</p></details><details style="background:#fff;border-radius:10px;padding:20px 24px;cursor:pointer;"><summary style="font-weight:700;font-size:15px;color:#1a1a1a;list-style:none;">¿Tiene garantía de devolución?</summary><p style="font-size:14px;color:#555;margin:12px 0 0;line-height:1.65;">Respuesta a la segunda pregunta.</p></details><details style="background:#fff;border-radius:10px;padding:20px 24px;cursor:pointer;"><summary style="font-weight:700;font-size:15px;color:#1a1a1a;list-style:none;">¿Para quién está diseñado?</summary><p style="font-size:14px;color:#555;margin:12px 0 0;line-height:1.65;">Respuesta a la tercera pregunta.</p></details></div></section>`,
    },
    {
      id: "lp-guarantee",
      label: "🛡 Garantía",
      content: `<section style="padding:48px 24px;background:#f0fdf4;text-align:center;"><div style="max-width:580px;margin:0 auto;"><div style="font-size:48px;margin-bottom:12px;">🛡</div><h3 style="font-size:22px;font-weight:800;margin:0 0 10px;color:#166534;">Garantía de 30 días sin preguntas</h3><p style="font-size:15px;color:#166534;opacity:.8;line-height:1.65;margin:0;">Si en los próximos 30 días no estás satisfecho, te devolvemos el 100% de tu inversión.</p></div></section>`,
    },
    {
      id: "lp-social-proof-bar",
      label: "✅ Barra prueba social",
      content: `<div style="padding:20px 24px;background:#f8f8fb;border-top:1px solid #eee;border-bottom:1px solid #eee;"><div style="max-width:960px;margin:0 auto;display:flex;flex-wrap:wrap;justify-content:center;align-items:center;gap:32px;"><span style="font-size:13px;color:#444;font-weight:500;">✅ +2,000 clientes satisfechos</span><span style="font-size:13px;color:#444;font-weight:500;">⭐ 4.9/5 valoración media</span><span style="font-size:13px;color:#444;font-weight:500;">🏆 Líderes en el sector</span><span style="font-size:13px;color:#444;font-weight:500;">🔒 Pago 100% seguro</span></div></div>`,
    },
    {
      id: "lp-webinar-form",
      label: "📋 Form webinar",
      content: `<section style="padding:64px 24px;background:#fff;text-align:center;"><h2 style="font-size:clamp(22px,3vw,34px);font-weight:800;margin:0 0 10px;color:#1a1a1a;">Reserva tu lugar gratis</h2><p style="color:#666;font-size:15px;margin:0 0 32px;">Solo quedan plazas limitadas. Regístrate ahora.</p><form action="#" method="post" style="max-width:440px;margin:0 auto;display:flex;flex-direction:column;gap:14px;"><input type="text" name="nombre" placeholder="Tu nombre completo" style="padding:14px 16px;border:1px solid #ddd;border-radius:8px;font-size:15px;width:100%;box-sizing:border-box;"/><input type="email" name="email" placeholder="tu@email.com" style="padding:14px 16px;border:1px solid #ddd;border-radius:8px;font-size:16px;width:100%;box-sizing:border-box;"/><input type="tel" name="telefono" placeholder="+34 600 000 000" style="padding:14px 16px;border:1px solid #ddd;border-radius:8px;font-size:16px;width:100%;box-sizing:border-box;"/><button type="submit" style="background:#6C63FF;color:#fff;padding:16px;border:none;border-radius:8px;font-size:16px;font-weight:700;cursor:pointer;width:100%;">¡Quiero mi lugar →</button></form><p style="font-size:12px;color:#aaa;margin:14px 0 0;">Sin spam. Puedes cancelar cuando quieras.</p></section>`,
    },
    {
      id: "lp-footer",
      label: "🔻 Footer",
      content: `<footer style="padding:32px 24px;background:#1a1a1a;text-align:center;"><p style="color:#555;font-size:12px;margin:0 0 10px;">© ${new Date().getFullYear()} Nombre de tu marca · Todos los derechos reservados</p><p style="font-size:12px;margin:0;"><a href="#" style="color:#555;text-decoration:none;margin:0 8px;">Privacidad</a><a href="#" style="color:#555;text-decoration:none;margin:0 8px;">Aviso legal</a><a href="#" style="color:#555;text-decoration:none;margin:0 8px;">Cookies</a></p></footer>`,
    },
    {
      id: "lp-divider",
      label: "─ Separador",
      content: `<div style="height:1px;background:#eeeeee;margin:0;width:100%;"></div>`,
    },
    {
      id: "lp-spacer",
      label: "↕ Espacio",
      content: `<div style="height:48px;width:100%;"></div>`,
    },
  ];
  blocks.forEach((b) =>
    bm.add(b.id, { label: b.label, category: "Landing", content: b.content, select: true })
  );
}

// ─── UPLOAD HELPERS ───────────────────────────────────────────────────────────

function buildUploadUrl() {
  const base = process.env.REACT_APP_API || "http://127.0.0.1:5001";
  return `${base}/company/upload-image`;
}

function getAuthToken() {
  try {
    const u = JSON.parse(localStorage.getItem("authUser") || "{}");
    return u?.token || "";
  } catch { return ""; }
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────

const LandingEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [formConfigOpen, setFormConfigOpen] = useState(false);

  const editorRef = useRef(null);
  const extractedScriptsRef = useRef([]);
  const containerRef = useRef(null);

  // Load page data
  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await GetData(`/pages/${id}`);
        setPage(response.data);
      } catch (err) {
        Swal.fire({ icon: "error", title: "Error", text: "No se pudo cargar la página" });
        navigate("/pages");
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [id, navigate]);

  // Initialize GrapesJS once page is loaded
  useEffect(() => {
    if (loading || !page || !containerRef.current) return;

    const html = page.content?.html || "";
    if (!html) {
      Swal.fire({
        icon: "warning",
        title: "Sin contenido",
        text: "Esta página aún no tiene HTML generado. Espera a que termine la generación.",
      });
      navigate("/pages");
      return;
    }

    // Destroy previous instance if any
    if (editorRef.current) {
      try { editorRef.current.destroy(); } catch (_) {}
      editorRef.current = null;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Extract body style for re-application to iframe body
    const origBodyStyle = doc.body ? doc.body.getAttribute("style") || "" : "";

    // Extract all <style> tags as protectedCss
    let protectedCss = "";
    doc.querySelectorAll("style").forEach((s) => {
      protectedCss += s.textContent + "\n";
      s.remove();
    });

    // Extract body{} rules to apply to iframe body
    let bodyRulesForIframe = "";
    const bodyRuleRe = /body\s*\{([^}]+)\}/gi;
    let bm;
    while ((bm = bodyRuleRe.exec(protectedCss)) !== null) {
      bodyRulesForIframe += bm[1] + ";";
    }

    // Sanitize CSS
    protectedCss = sanitizeCssForEditor(protectedCss);

    const editorFixCss = `
/* GrapesJS editor overrides */
[style*="opacity:0"], [style*="opacity: 0"],
[style*="visibility:hidden"], [style*="visibility: hidden"] {
  opacity: 1 !important;
  visibility: visible !important;
}
*, *::before, *::after {
  animation: none !important;
  transition: none !important;
}
`;

    // Extract scripts (preserve id for form config scripts)
    extractedScriptsRef.current = [];
    doc.querySelectorAll("script").forEach((s) => {
      extractedScriptsRef.current.push({
        id: s.id || null,
        src: s.src || null,
        content: s.textContent || "",
      });
      s.remove();
    });

    // Sanitize DOM
    sanitizeDomForEditor(doc);

    // Get body HTML after removing style/script/event attrs
    let bodyHtml = doc.body ? doc.body.innerHTML : html;
    bodyHtml = bodyHtml.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");

    // Collect external stylesheets
    const canvasStyles = [];
    doc.querySelectorAll('link[rel="stylesheet"]').forEach((l) => {
      if (l.href) canvasStyles.push(l.href);
    });

    const editor = grapesjs.init({
      container: containerRef.current,
      height: "100%",
      width: "100%",
      storageManager: false,
      components: bodyHtml,
      style: "",
      protectedCss,
      canvas: { styles: canvasStyles, scripts: [] },
      avoidInlineStyle: false,
      deviceManager: {
        devices: [
          // No explicit height → GrapesJS auto-sizes iframe to content
          // Combined with the canvas overflow fix below this allows natural scrolling
          { name: "Desktop", width: "" },
          { name: "Tablet", width: "768px", widthMedia: "768px" },
          { name: "Mobile", width: "390px", widthMedia: "390px" },
        ],
      },
      assetManager: {
        upload: buildUploadUrl(),
        uploadName: "file",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        multiUpload: false,
        autoAdd: true,
      },
      styleManager: {
        sectors: [
          {
            name: "Dimensiones",
            open: false,
            properties: [
              "width", "max-width", "min-width", "height", "min-height", "max-height",
              "padding", "padding-top", "padding-right", "padding-bottom", "padding-left",
              "margin", "margin-top", "margin-right", "margin-bottom", "margin-left",
            ],
          },
          {
            name: "Tipografía",
            open: false,
            properties: [
              "font-family", "font-size", "font-weight", "font-style", "color",
              "text-align", "line-height", "letter-spacing", "text-decoration", "text-transform",
              { name: "Sombra texto", property: "text-shadow", type: "base", defaults: "none" },
            ],
          },
          {
            name: "Fondo",
            open: false,
            properties: [
              { name: "Fondo (color/degradado)", property: "background", type: "base", defaults: "none" },
              "background-color",
              { name: "Imagen de fondo", property: "background-image", type: "base", defaults: "none" },
              "background-size", "background-position", "background-repeat",
              { name: "Clip fondo", property: "background-clip", type: "base", defaults: "" },
            ],
          },
          {
            name: "Borde y sombra",
            open: false,
            properties: [
              "border", "border-radius",
              { name: "Sombra caja", property: "box-shadow", type: "base", defaults: "none" },
              { name: "Contorno", property: "outline", type: "base", defaults: "none" },
            ],
          },
          {
            name: "Efectos",
            open: false,
            properties: [
              { name: "Opacidad", property: "opacity", type: "slider", defaults: 1, min: 0, max: 1, step: 0.01 },
              { name: "Filtro CSS", property: "filter", type: "base", defaults: "" },
              { name: "Filtro fondo", property: "backdrop-filter", type: "base", defaults: "" },
              { name: "Transformar", property: "transform", type: "base", defaults: "" },
              { name: "Transición", property: "transition", type: "base", defaults: "" },
              { name: "Mix blend", property: "mix-blend-mode", type: "base", defaults: "" },
            ],
          },
          {
            name: "Display",
            open: false,
            properties: [
              "display", "flex-direction", "justify-content", "align-items",
              "flex-wrap", "gap", "grid-template-columns", "align-self", "flex", "overflow",
            ],
          },
          {
            name: "Posición",
            open: false,
            properties: [
              "position", "top", "right", "bottom", "left",
              "z-index", "float", "cursor", "pointer-events",
            ],
          },
        ],
      },
      pluginsOpts: {},
    });

    // On editor load: inject CSS into iframe head, apply body styles
    editor.on("load", () => {
      // Widen sidebar
      const vc = editor.Panels.getPanel("views-container");
      if (vc) vc.set("width", "300px");

      try {
        const iframeDoc = editor.Canvas.getDocument();
        if (iframeDoc) {
          if (protectedCss && !iframeDoc.getElementById("gjs-landing-styles")) {
            const s = iframeDoc.createElement("style");
            s.id = "gjs-landing-styles";
            s.textContent = protectedCss;
            (iframeDoc.head || iframeDoc.documentElement).appendChild(s);
          }
          if (bodyRulesForIframe || origBodyStyle) {
            iframeDoc.body.setAttribute(
              "style",
              (origBodyStyle + ";" + bodyRulesForIframe).replace(/;;+/g, ";")
            );
          }
          // Fix scroll: landing pages often set overflow:hidden on html/body
          // which prevents GrapesJS canvas from scrolling. Force it open.
          iframeDoc.documentElement.style.setProperty("overflow", "auto", "important");
          iframeDoc.documentElement.style.setProperty("height", "auto", "important");
          iframeDoc.body.style.setProperty("overflow-y", "auto", "important");
          iframeDoc.body.style.setProperty("overflow-x", "hidden", "important");
          iframeDoc.body.style.setProperty("height", "auto", "important");

          if (!iframeDoc.getElementById("gjs-editor-fixes")) {
            const fix = iframeDoc.createElement("style");
            fix.id = "gjs-editor-fixes";
            fix.textContent = editorFixCss;
            (iframeDoc.head || iframeDoc.documentElement).appendChild(fix);
          }
          iframeDoc.querySelectorAll("script:not([src*='grapes'])").forEach((s) => s.remove());
          sanitizeDomForEditor(iframeDoc);

          // Re-inject page scripts so countdowns, sticky CTAs, etc. work in preview.
          // Skip patterns that would destroy the GrapesJS DOM.
          const UNSAFE_SCRIPT =
            /document\.currentScript\.parentElement|document\.write\s*\(/;
          extractedScriptsRef.current.forEach(({ src, content }) => {
            try {
              const sc = iframeDoc.createElement("script");
              if (src) {
                sc.src = src;
              } else if (content && !UNSAFE_SCRIPT.test(content)) {
                sc.textContent = content;
              } else {
                return;
              }
              iframeDoc.body.appendChild(sc);
            } catch (_) {}
          });
        }
      } catch (_) {}
    });

    // Fix GrapesJS canvas scroll: grapes.min.css sets overflow:hidden on .gjs-cv-canvas
    // which prevents the canvas wrapper from scrolling even if the iframe content is tall.
    const canvasScrollStyle = document.createElement("style");
    canvasScrollStyle.setAttribute("data-gjs-canvas-fix", "1");
    canvasScrollStyle.textContent =
      ".gjs-cv-canvas { overflow-y: auto !important; height: 100% !important; }";
    document.head.appendChild(canvasScrollStyle);

    registerLandingBlocks(editor);

    // Auto-upload to Azure: our endpoint returns { data: { url } } which GrapesJS
    // won't auto-process (it expects an array), so we add the asset manually.
    editor.on("asset:upload:response", (response) => {
      const url = response?.data?.url;
      if (url) {
        editor.AssetManager.add([{ type: "image", src: url }]);
      }
    });
    editor.on("asset:upload:error", () => {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo subir la imagen. Verifica el formato." });
    });

    editorRef.current = editor;

    return () => {
      document.querySelector("style[data-gjs-canvas-fix]")?.remove();
      if (editorRef.current) {
        try { editorRef.current.destroy(); } catch (_) {}
        editorRef.current = null;
      }
    };
  }, [loading, page, navigate]);

  const handleDownload = useCallback(() => {
    if (!editorRef.current) return;
    const editor = editorRef.current;

    // Rebuild full HTML — same logic as handleSave but no network call
    const originalHtml = page.content?.html || "";
    const parser = new DOMParser();
    const origDoc = parser.parseFromString(originalHtml, "text/html");

    const newCss = editor.getCss();
    if (newCss && newCss.trim()) {
      const existing = origDoc.querySelector("style[data-gjs-editable]");
      if (existing) {
        existing.textContent = newCss;
      } else {
        const styleEl = origDoc.createElement("style");
        styleEl.setAttribute("data-gjs-editable", "1");
        styleEl.textContent = newCss;
        origDoc.head.appendChild(styleEl);
      }
    }

    origDoc.body.innerHTML = editor.getHtml();

    extractedScriptsRef.current.forEach(({ id, src, content }) => {
      const sc = origDoc.createElement("script");
      if (id) sc.id = id;
      if (src) { sc.src = src; } else if (content) { sc.textContent = content; }
      origDoc.body.appendChild(sc);
    });

    const fullHtml = "<!DOCTYPE html>\n" + origDoc.documentElement.outerHTML;

    // Trigger browser download
    const blob = new Blob([fullHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeName = (page?.name || "landing").replace(/[^a-z0-9_-]/gi, "_").toLowerCase();
    a.download = `${safeName}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [page, extractedScriptsRef]);

  const handleSave = useCallback(async () => {
    if (!editorRef.current) return;
    setSaving(true);
    try {
      const editor = editorRef.current;

      // Rebuild full HTML from original (preserving <head>)
      const originalHtml = page.content?.html || "";
      const parser = new DOMParser();
      const origDoc = parser.parseFromString(originalHtml, "text/html");

      // Update/add editable style tag in head
      const newCss = editor.getCss();
      if (newCss && newCss.trim()) {
        const existing = origDoc.querySelector("style[data-gjs-editable]");
        if (existing) {
          existing.textContent = newCss;
        } else {
          const styleEl = origDoc.createElement("style");
          styleEl.setAttribute("data-gjs-editable", "1");
          styleEl.textContent = newCss;
          origDoc.head.appendChild(styleEl);
        }
      }

      // Replace body content with GrapesJS output
      origDoc.body.innerHTML = editor.getHtml();

      // Re-inject extracted scripts at end of body (preserving id for form config)
      extractedScriptsRef.current.forEach(({ id, src, content }) => {
        const sc = origDoc.createElement("script");
        if (id) sc.id = id;
        if (src) { sc.src = src; } else if (content) { sc.textContent = content; }
        origDoc.body.appendChild(sc);
      });

      const reconstructedHtml = "<!DOCTYPE html>\n" + origDoc.documentElement.outerHTML;

      // Save to backend — no `brief` field means no AI regeneration
      await PutData(`/pages/${id}`, {
        content: { ...page.content, html: reconstructedHtml },
      });

      Swal.fire({ icon: "success", title: "Guardado", text: "Los cambios han sido guardados", timer: 1500, showConfirmButton: false });
    } catch (err) {
      console.error("Error saving page:", err);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudieron guardar los cambios" });
    } finally {
      setSaving(false);
    }
  }, [id, page]);

  // ─── Apply form config: inject scripts + save page ──────────────────────────

  const handleApplyFormConfig = useCallback(async ({ contactListId, apiKey, endpoint, createdBy, redirectUrl }) => {
    if (!editorRef.current) return;
    setSaving(true);
    try {
      const editor = editorRef.current;

      // 1. Build config + handler script contents
      const configContent = `window.__LP_FORM__ = ${JSON.stringify({
        endpoint,
        apiKey,
        createdBy,
        redirectUrl: redirectUrl || null,
      })};`;

      // 2. Update extractedScriptsRef: replace any existing form scripts
      const filtered = extractedScriptsRef.current.filter(
        (s) => s.id !== "lp-form-config" && s.id !== "lp-form-handler"
      );
      filtered.push({ id: "lp-form-config", src: null, content: configContent });
      filtered.push({ id: "lp-form-handler", src: null, content: LP_FORM_HANDLER_SCRIPT });
      extractedScriptsRef.current = filtered;

      // 3. Rebuild full HTML (same as handleSave) with new scripts included
      const originalHtml = page.content?.html || "";
      const parser = new DOMParser();
      const origDoc = parser.parseFromString(originalHtml, "text/html");

      const newCss = editor.getCss();
      if (newCss && newCss.trim()) {
        const existing = origDoc.querySelector("style[data-gjs-editable]");
        if (existing) {
          existing.textContent = newCss;
        } else {
          const styleEl = origDoc.createElement("style");
          styleEl.setAttribute("data-gjs-editable", "1");
          styleEl.textContent = newCss;
          origDoc.head.appendChild(styleEl);
        }
      }

      origDoc.body.innerHTML = editor.getHtml();

      extractedScriptsRef.current.forEach(({ id, src, content }) => {
        const sc = origDoc.createElement("script");
        if (id) sc.id = id;
        if (src) { sc.src = src; } else if (content) { sc.textContent = content; }
        origDoc.body.appendChild(sc);
      });

      const reconstructedHtml = "<!DOCTYPE html>\n" + origDoc.documentElement.outerHTML;

      // 4. Single PUT: content + page metadata
      await PutData(`/pages/${id}`, {
        content: { ...page.content, html: reconstructedHtml },
        contactListId,
        redirectUrl: redirectUrl || undefined,
      });

      // Update local page state so subsequent saves preserve metadata
      setPage((prev) => ({ ...prev, contactListId, redirectUrl: redirectUrl || prev?.redirectUrl }));

      Swal.fire({
        icon: "success",
        title: "Formulario configurado",
        text: "Script inyectado y página guardada correctamente",
        timer: 2000,
        showConfirmButton: false,
      });
      setFormConfigOpen(false);
    } catch (err) {
      console.error("Error applying form config:", err);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo guardar la configuración del formulario" });
    } finally {
      setSaving(false);
    }
  }, [id, page]);

  const handleTogglePreview = useCallback(() => {
    if (!editorRef.current) return;
    if (previewMode) {
      editorRef.current.stopCommand("preview");
    } else {
      editorRef.current.runCommand("preview");
    }
    setPreviewMode((prev) => !prev);
  }, [previewMode]);

  const handleExit = useCallback(() => {
    navigate("/pages");
  }, [navigate]);

  if (loading) {
    return (
      <div
        style={{
          position: "fixed", inset: 0, background: "#1a1a2e",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexDirection: "column", gap: 16, color: "#fff",
        }}
      >
        <div className="spinner-border text-light" />
        <span>Cargando editor...</span>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", background: "#1a1a2e", zIndex: 9999 }}>
      {/* Top bar */}
      <div
        style={{
          height: 48, background: "#0d0d1a", borderBottom: "1px solid #2a2a4a",
          display: "flex", alignItems: "center", padding: "0 16px", gap: 12, flexShrink: 0,
        }}
      >
        <button
          onClick={handleExit}
          style={{
            background: "transparent", border: "1px solid #3a3a5a", color: "#aaa",
            borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 13,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <i className="mdi mdi-arrow-left" /> Salir
        </button>

        <span style={{ color: "#ccc", fontSize: 14, fontWeight: 600, flexGrow: 1 }}>
          {page?.name || "Editor visual"}
        </span>

        <button
          onClick={() => setFormConfigOpen(true)}
          style={{
            background: "transparent", border: "1px solid #3a3a5a", color: "#ccc",
            borderRadius: 6, padding: "4px 14px", cursor: "pointer", fontSize: 13,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <i className="mdi mdi-link-variant" /> Formulario
        </button>

        <button
          onClick={handleTogglePreview}
          style={{
            background: "transparent", border: "1px solid #3a3a5a", color: "#ccc",
            borderRadius: 6, padding: "4px 14px", cursor: "pointer", fontSize: 13,
          }}
        >
          {previewMode ? (
            <><i className="mdi mdi-pencil" /> Editar</>
          ) : (
            <><i className="mdi mdi-eye" /> Previsualizar</>
          )}
        </button>

        <button
          onClick={handleDownload}
          title="Descargar HTML completo"
          style={{
            background: "transparent", border: "1px solid #3a3a5a", color: "#ccc",
            borderRadius: 6, padding: "4px 14px", cursor: "pointer", fontSize: 13,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <i className="mdi mdi-download" /> Descargar
        </button>

        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: "#6C63FF", border: "none", color: "#fff",
            borderRadius: 6, padding: "6px 20px", cursor: saving ? "not-allowed" : "pointer",
            fontSize: 13, fontWeight: 700, opacity: saving ? 0.7 : 1,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          {saving ? (
            <><span className="spinner-border spinner-border-sm" style={{ width: "0.8rem", height: "0.8rem" }} /> Guardando...</>
          ) : (
            <><i className="mdi mdi-content-save" /> Guardar</>
          )}
        </button>
      </div>

      {/* GrapesJS container */}
      <div ref={containerRef} style={{ flexGrow: 1, overflow: "hidden" }} />

      {/* Form config modal */}
      <FormConfigModal
        isOpen={formConfigOpen}
        toggle={() => setFormConfigOpen(false)}
        page={page}
        onApply={handleApplyFormConfig}
      />
    </div>
  );
};

export default LandingEditor;
